import React, { useState, useEffect } from "react";
import {
  styled,
  useTheme,
  Theme,
  CSSObject,
  createTheme,
  ThemeProvider,
} from "@mui/material/styles";
import {
  Box,
  Drawer as MuiDrawer,
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Stack,
  Menu,
  MenuItem,
  useMediaQuery,
  SwipeableDrawer,
  Link,
  Tooltip,
  InputAdornment,
  TextField,
  Autocomplete,
  Collapse,
  alpha,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  CalendarMonth as CalendarMonthIcon,
  Wc as WcIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccountCircle as AccountCircleIcon,
  Close as CloseIcon,
  ArrowForwardIosSharp as ArrowForwardIosSharpIcon,
  // --- NEW ICONS FOR GREETING ---
  WbSunnyOutlined as WbSunnyOutlinedIcon,
  NightsStayOutlined as NightsStayOutlinedIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useUserId } from "../../utils/Constant";
import DrawerLogo from "../../assets/images/DrawerLogo.png";
import AppbarLogo from "../../assets/images/recyclebinLogo.png";

// --- PROFESSIONAL THEME ---
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#1A365D",
      light: "#2A4365",
      dark: "#152c4d",
    },
    secondary: {
      main: "#ED8936",
    },
    background: {
      default: "#F7FAFC",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#2D3748",
      secondary: "#718096",
    },
    success: { main: '#38A169' },
    error: { main: '#E53E3E' },
  },
  typography: {
    fontFamily: "Inter, Roboto, sans-serif",
    h6: { fontWeight: 600 },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#2D3748",
          color: "white",
          fontSize: '12px',
          padding: '8px 12px',
          borderRadius: '6px',
        },
        arrow: { color: "#2D3748" },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '&:hover': { backgroundColor: alpha("#1A365D", 0.04) },
        },
      },
    },
  },
});

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  borderRight: 'none',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  borderRight: 'none',
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: alpha(theme.palette.primary.main, 0.85),
  backdropFilter: "blur(10px)",
  boxShadow: 'none',
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  color: theme.palette.common.white,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

interface MenuItem {
  [x: string]: any;
}

function MiniDrawerComponent({ items }: { items: MenuItem[] }) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = React.useState(!isMobile);
  const [profileDrawerOpen, setProfileDrawerOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openMenus, setOpenMenus] = React.useState<number[]>([]);
  const [, setUserId] = useUserId();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };
  
  // --- NEW FUNCTION FOR GREETING ICON ---
  const getGreetingIcon = () => {
    const hour = new Date().getHours();
    const iconProps = { sx: { fontSize: '1.4rem', mr: 1, color: theme.palette.secondary.main } };
    if (hour < 18) { // Morning & Afternoon
      return <WbSunnyOutlinedIcon {...iconProps} />;
    }
    // Evening
    return <NightsStayOutlinedIcon {...iconProps} />;
  };
  
  React.useEffect(() => {
    const activeParentIndices = items
      .map((parent, index) => 
        parent.items?.some((child:any) => location.pathname.startsWith(child.path)) ? index : -1)
      .filter(index => index !== -1);
    setOpenMenus(activeParentIndices);
  }, [items, location.pathname]);

  const userData = JSON.parse(localStorage.getItem("userdata")!) || {};
  const userDetail = userData[0]?.userdetail || [];
  const menudata = userDetail[0] || {};
  const username = `${menudata.firsT_NAME || ""} ${menudata.suR_NAME || ""}`.trim();

  const [currentTime, setCurrentTime] = React.useState(new Date());
  React.useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formattedDate = dayjs(currentTime).format("ddd, DD MMM YYYY");
  const formattedTime = dayjs(currentTime).format("hh:mm A");

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) setOpen(false);
  };

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    setUserId('');
    navigate("/");
  };
  
  const handleCollapseToggle = (index: number) => {
    setOpenMenus(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
  };

  const getGenderText = (genderId: number) => ({ 1: "Male", 2: "Female", 3: "Other" }[genderId] || "Unknown");
  const getFirstLetters = (text: string) => text.split(' ').map(word => word.charAt(0)).join('').toUpperCase();
  const handleRightClick = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(path, "_blank");
  };

  const renderDrawerContent = () => (
    <>
      <DrawerHeader sx={{ justifyContent: "space-between", pl: 3, pr: 1 }}>
        {open && <img src={DrawerLogo} style={{ height: 45, objectFit: 'contain' }} alt="Logo" />}
        <IconButton onClick={handleDrawerClose}>
          <ChevronLeftIcon />
        </IconButton>
      </DrawerHeader>
      
      {open && (
        <Box sx={{ p: 2, pt: 1 }}>
          <Autocomplete
            freeSolo fullWidth size="small"
            options={items.flatMap(item => item.items?.map((subItem:any) => ({label: subItem.name, path: subItem.path})) || [])}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
            onChange={(_, value) => {
                if(!value || typeof value === 'string') return;
                handleNavigation(value.path);
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search Menu..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: '8px', fontSize: '0.9rem' }
                }}
              />
            )}
          />
        </Box>
      )}

      <List sx={{ p: '0 8px', overflowY: 'auto' }}>
        <ListItem disablePadding sx={{ my: 1 }}>
          <Tooltip title="Home" placement="right" arrow disableHoverListener={open}>
            <ListItemButton
              onClick={() => handleNavigation("/home")}
              sx={{
                justifyContent: open ? "initial" : "center",
                bgcolor: location.pathname === "/home" ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                color: location.pathname === "/home" ? 'primary.main' : 'text.secondary',
                '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main'
                },
                fontWeight: location.pathname === "/home" ? 600 : 500
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: open ? 2 : "auto", justifyContent: "center", color: 'inherit' }}>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Home" sx={{ opacity: open ? 1 : 0 }} primaryTypographyProps={{ fontWeight: 'inherit' }} />
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {items.map((item, index) => {
          const isParentActive = item.items?.some((sub:any) => location.pathname.startsWith(sub.path));
          const isMenuOpen = openMenus.includes(index);
          return (
            <React.Fragment key={item.id}>
              <ListItem disablePadding sx={{ my: 0.5 }}>
               <Tooltip title={item.name} placement="right" arrow disableHoverListener={open}>
                <ListItemButton 
                  onClick={() => handleCollapseToggle(index)}
                  sx={{ justifyContent: open ? 'initial' : 'center', color: isParentActive ? 'primary.dark' : 'text.primary' }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: open ? 2 : "auto", justifyContent: "center"}}>
                     <Avatar sx={{ width: 32, height: 32, bgcolor: isParentActive ? 'primary.main' : 'background.default', color: isParentActive ? 'common.white' : 'text.primary', fontSize: '0.9rem', fontWeight: 600 }}>
                       {getFirstLetters(item.name)}
                     </Avatar>
                  </ListItemIcon>
                  <ListItemText primary={item.name} sx={{ opacity: open ? 1 : 0 }} primaryTypographyProps={{ fontWeight: 600 }}/>
                  {open && (<ExpandMoreIcon sx={{ transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />)}
                </ListItemButton>
                </Tooltip>
              </ListItem>

              <Collapse in={isMenuOpen} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ pl: open ? '16px' : 0, py: '4px' }}>
                  {item.items?.map((subItem:any) => {
                    const isActive = location.pathname.startsWith(subItem.path);
                    return (
                      <ListItem key={subItem.id} disablePadding sx={{ display: "block", my: '2px' }}>
                        <Tooltip title={subItem.name} placement="right" arrow disableHoverListener={open}>
                        <ListItemButton
                          onClick={() => handleNavigation(subItem.path)}
                          onContextMenu={handleRightClick(subItem.path)}
                          sx={{
                            justifyContent: open ? "initial" : "center",
                            pl: open ? 3 : 'auto',
                            borderRadius: '50px', mx: 1,
                            bgcolor: isActive ? 'secondary.main' : "transparent",
                            color: isActive ? "common.white" : "text.secondary",
                            transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
                            "&:hover": {
                              bgcolor: isActive ? theme.palette.secondary.dark : alpha(theme.palette.primary.main, 0.08),
                              color: isActive ? "common.white" : 'primary.main',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 0, mr: open ? 1.5 : 'auto', justifyContent: "center", color: 'inherit' }}>
                             <ArrowForwardIosSharpIcon sx={{ fontSize: '0.6rem' }} />
                          </ListItemIcon>
                          <ListItemText primary={subItem.name} primaryTypographyProps={{ variant: 'body2', fontWeight: isActive ? 600 : 500 }} sx={{ opacity: open ? 1 : 0 }} />
                        </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex", bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit" aria-label="open drawer" onClick={handleDrawerOpen} edge="start"
            sx={{ mr: 2, ...(open && !isMobile && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>

          {/* --- LEFT: GREETING SECTION --- */}
          <Stack direction="row" alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
            {getGreetingIcon()}
            <Stack>
                <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.8), lineHeight: 1.2 }}>
                    {getGreeting()}
                </Typography>
               {/* <Typography variant="body1" noWrap fontWeight={700} lineHeight={1.2}>
                    {username || "Valued User"}
                </Typography> */ }
            </Stack>
          </Stack>
          
          {/* --- CENTER: PROJECT NAME --- */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <Typography variant="h6" fontWeight={700}>
                {"GSV Central Library (CSJMU)" }
            </Typography>
          </Box>

          {/* --- RIGHT: ACTIONS (TIME + PROFILE) --- */}
           <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
                <Stack alignItems="flex-end" sx={{ display: { xs: 'none', md: 'flex' } }}>
                    <Typography variant="body2" fontWeight={600} color="common.white">{formattedTime}</Typography>
                    <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.8) }}>{formattedDate}</Typography>
                </Stack>
                <Divider 
                    orientation="vertical" flexItem 
                    sx={{ display: { xs: 'none', md: 'block' }, borderColor: alpha(theme.palette.common.white, 0.2) }} 
                />
              <Tooltip title={username || "User Profile"}>
                <IconButton onClick={handleMenuClick} size="small">
                    <Avatar sx={{ width: 40, height: 40, bgcolor: 'secondary.main', color: 'common.white', fontWeight: 600 }}>
                    {username.charAt(0).toUpperCase()}
                    </Avatar>
                </IconButton>
              </Tooltip>
           </Stack>

          {/* Profile Menu Popup */}
          <Menu
            anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: "visible", filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
                mt: 1.5, borderRadius: '10px', width: 200,
                "& .MuiAvatar-root": { width: 32, height: 32, ml: -0.5, mr: 1 },
                "&::before": {
                  content: '""', display: "block", position: "absolute",
                  top: 0, right: 14, width: 10, height: 10,
                  bgcolor: "background.paper", transform: "translateY(-50%) rotate(45deg)", zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body1" fontWeight={600}>{username}</Typography>
                <Typography variant="body2" color="text.secondary">{menudata.rolename}</Typography>
            </Box>
            <Divider sx={{ my: 1 }}/>
            <MenuItem onClick={() => { handleMenuClose(); setProfileDrawerOpen(true); }}>
              <ListItemIcon> <AccountCircleIcon fontSize="small" /> </ListItemIcon>
              My Profile
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon> <SettingsIcon fontSize="small" /> </ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon> <LogoutIcon fontSize="small" color="error" /> </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      {/* Drawer Variants */}
      {isMobile ? (
        <SwipeableDrawer
            variant="temporary" open={open} onClose={handleDrawerClose} onOpen={handleDrawerOpen}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', borderRight: 'none', bgcolor: 'background.paper' } }}
        >
          {renderDrawerContent()}
        </SwipeableDrawer>
      ) : (
        <Drawer variant="permanent" open={open} sx={{ '& .MuiDrawer-paper': { bgcolor: 'background.paper' } }}>
          {renderDrawerContent()}
        </Drawer>
      )}

      {/* Profile Details Drawer */}
      <SwipeableDrawer
        anchor="right" open={profileDrawerOpen} onClose={() => setProfileDrawerOpen(false)} onOpen={() => setProfileDrawerOpen(true)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 360 }, bgcolor: 'background.default' } }}
        style={{zIndex:"1400"}}
      >
        <Box sx={{ p: 2, height: '100%' }} role="presentation">
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                 <Typography variant="h6">My Profile</Typography>
                 <IconButton onClick={() => setProfileDrawerOpen(false)}><CloseIcon /></IconButton>
            </Stack>
            <Divider />
          
          {userDetail.map((user: any, index: number) => (
            <Box key={index} mt={2}>
              <Stack alignItems="center" spacing={1} mb={3}
                 sx={{ p:3, borderRadius: 2, background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`, color: 'common.white' }}
              >
                <Avatar sx={{ width: 90, height: 90, bgcolor: 'secondary.main', fontSize: '2.5rem', mb: 1, border: '4px solid white' }}>
                  {`${user.firsT_NAME?.charAt(0) || ''}${user.suR_NAME?.charAt(0) || ''}`}
                </Avatar>
                <Typography variant="h6" fontWeight={700}>{`${user.firsT_NAME} ${user.suR_NAME}`}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>{user.rolename}</Typography>
              </Stack>
              
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    User Details
                </Typography>
                <List dense>
                    {[
                        { icon: <PersonIcon color="primary" />, label: "Login Name", value: user.logiN_NAME || 'N/A' },
                        { icon: <AdminPanelSettingsIcon color="primary" />, label: "Role", value: user.rolename || 'N/A' },
                        { icon: <CalendarMonthIcon color="primary" />, label: "Date of Birth", value: dayjs(user.dob).format("DD MMMM YYYY") || 'N/A' },
                        { icon: <WcIcon color="primary" />, label: "Gender", value: getGenderText(user.gendeR_ID) },
                        { icon: <PhoneIcon color="primary" />, label: "Mobile", value: user.cuR_MOBILE || 'N/A' },
                        { icon: <EmailIcon color="primary" />, label: "Email", value: user.email || 'N/A' },
                        { 
                            icon: user.iS_ACTIVE ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />, 
                            label: "Account Status", 
                            value: user.iS_ACTIVE ? 'Active' : 'Inactive',
                            valueColor: user.iS_ACTIVE ? 'success.main' : 'error.main'
                        },
                    ].map(item => (
                        <ListItem key={item.label} sx={{ py: 1.5 }} divider>
                            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.label} secondary={<Typography variant="body2" fontWeight={600} color={item.valueColor || "text.primary"}>{item.value}</Typography>} />
                        </ListItem>
                    ))}
                </List>
              </Box>
            </Box>
          ))}
        </Box>
      </SwipeableDrawer>
    </Box>
  );
}

// --- WRAPPER COMPONENT ---
export default function ThemedMiniDrawer({ items }: { items: MenuItem[] }) {
    return (
        <ThemeProvider theme={customTheme}>
            <MiniDrawerComponent items={items} />
        </ThemeProvider>
    )
}
